import { useState } from 'react';
import { api, type UtenteGestito } from '../api';
import { Campo, Caricamento, Dialogo, Errore, Etichetta, Scheda, useAvvisi, useCarico } from '../componenti/base';
import { formattaData } from '../formato';
import { useSessione } from '../main';

const RUOLI = [
  { valore: 'OPERATORE' as const, nome: 'Operatore', spiega: 'Usa Mastro: importa, riconcilia, categorizza, pianifica.' },
  { valore: 'ADMIN' as const, nome: 'Amministratore', spiega: 'Come l’operatore, e in più gestisce le persone.' },
];

/**
 * Le persone che possono entrare in Mastro.
 *
 * Chi ha già lavorato non si elimina, si **disattiva**: cancellarlo farebbe
 * sparire il nome da sotto le decisioni che ha preso. Si può eliminare solo chi
 * non è mai entrato — cioè un indirizzo sbagliato al momento della creazione.
 */
export function Utenti() {
  const { utente, organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const [nuovo, setNuovo] = useState(false);
  const [reimposta, setReimposta] = useState<UtenteGestito | null>(null);
  const { dati, caricamento, errore, ricarica } = useCarico(() => api.get<UtenteGestito[]>('/api/utenti'), []);

  const cambia = async (u: UtenteGestito, corpo: Record<string, unknown>, messaggio: string) => {
    try {
      await api.put(`/api/utenti/${u.id}`, corpo);
      avvisa(messaggio);
      ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Modifica non riuscita.', 'errore');
    }
  };

  const elimina = async (u: UtenteGestito) => {
    if (!confirm(`Eliminare ${u.email}? Non è mai entrato, quindi non lascia niente dietro di sé.`)) return;
    try {
      await api.del(`/api/utenti/${u.id}`);
      avvisa('Utente eliminato.');
      ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Eliminazione non riuscita.', 'errore');
    }
  };

  if (caricamento) return <Caricamento />;
  if (errore) return <div className="p-6"><Errore testo={errore} riprova={ricarica} /></div>;

  const elenco = dati ?? [];

  return (
    <div className="pagina space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Utenti</h1>
          <p className="text-sm text-inchiostro-500">
            Chi può entrare in {organizzazione.ragioneSociale}. Due ruoli: l’operatore usa Mastro, l’amministratore
            gestisce anche le persone.
          </p>
        </div>
        <button className="bottone-primario" onClick={() => setNuovo(true)}>
          Nuovo utente
        </button>
      </header>

      <Scheda senzaPadding>
        <div className="overflow-x-auto">
          <table className="dati">
            <thead>
              <tr>
                <th>Persona</th>
                <th>Ruolo</th>
                <th>Ultimo accesso</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {elenco.map((u) => {
                const sonoIo = u.id === utente.id;
                return (
                  <tr key={u.id} className={u.attivo ? '' : 'text-inchiostro-400'}>
                    <td>
                      <span className="font-medium">{u.nome ?? u.email}</span>
                      {u.nome && <span className="ml-2 text-xs text-inchiostro-500">{u.email}</span>}
                      {sonoIo && <span className="ml-2"><Etichetta testo="sei tu" tono="info" /></span>}
                      {!u.attivo && <span className="ml-2"><Etichetta testo="disattivato" /></span>}
                      {u.deveCambiarePassword && u.attivo && (
                        <span className="ml-2">
                          <Etichetta
                            testo="password da cambiare"
                            tono="attenzione"
                          />
                        </span>
                      )}
                    </td>
                    <td>
                      <select
                        className="campo w-44 py-1 text-xs"
                        value={u.ruolo}
                        disabled={sonoIo}
                        title={sonoIo ? 'Il tuo ruolo te lo cambia un altro amministratore.' : undefined}
                        onChange={(e) =>
                          cambia(u, { ruolo: e.target.value }, `${u.email} ora è ${e.target.value === 'ADMIN' ? 'amministratore' : 'operatore'}.`)
                        }
                      >
                        {RUOLI.map((r) => (
                          <option key={r.valore} value={r.valore}>
                            {r.nome}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num whitespace-nowrap text-xs">
                      {u.ultimoAccesso ? (
                        formattaData(u.ultimoAccesso.slice(0, 10), organizzazione.formatoData)
                      ) : (
                        <span className="text-inchiostro-400">mai entrato</span>
                      )}
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <button className="bottone-secondario px-2 py-1 text-xs" onClick={() => setReimposta(u)}>
                          Password
                        </button>
                        {!sonoIo && (
                          <button
                            className="bottone-secondario px-2 py-1 text-xs"
                            onClick={() =>
                              cambia(
                                u,
                                { attivo: !u.attivo },
                                u.attivo ? `${u.email} non può più entrare.` : `${u.email} può entrare di nuovo.`,
                              )
                            }
                          >
                            {u.attivo ? 'Disattiva' : 'Riattiva'}
                          </button>
                        )}
                        {!sonoIo && !u.ultimoAccesso && (
                          <button className="bottone-secondario px-2 py-1 text-xs" onClick={() => elimina(u)}>
                            Elimina
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Scheda>

      <p className="text-xs text-inchiostro-500">
        Chi ha già usato Mastro si disattiva e non si elimina: cancellarlo farebbe sparire il suo nome da sotto le
        decisioni che ha preso. E deve sempre restare almeno un amministratore attivo — nessuno può togliersi i poteri
        da solo.
      </p>

      {nuovo && (
        <FormNuovoUtente
          onChiudi={() => setNuovo(false)}
          onCreato={() => {
            setNuovo(false);
            ricarica();
          }}
        />
      )}
      {reimposta && (
        <FormPassword
          utente={reimposta}
          onChiudi={() => setReimposta(null)}
          onFatto={() => {
            setReimposta(null);
            ricarica();
          }}
        />
      )}
    </div>
  );
}

function FormNuovoUtente({ onChiudi, onCreato }: { onChiudi: () => void; onCreato: () => void }) {
  const avvisa = useAvvisi();
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [ruolo, setRuolo] = useState<'ADMIN' | 'OPERATORE'>('OPERATORE');
  const [password, setPassword] = useState('');
  const [attesa, setAttesa] = useState(false);

  const crea = async () => {
    setAttesa(true);
    try {
      await api.post('/api/utenti', { email: email.trim(), nome: nome.trim() || null, ruolo, password });
      avvisa(`${email.trim().toLowerCase()} può entrare: comunicagli la password.`);
      onCreato();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Creazione non riuscita.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  return (
    <Dialogo aperto titolo="Nuovo utente" onChiudi={onChiudi}>
      <div className="space-y-3">
        <Campo etichetta="Indirizzo email" aiuto="È anche il nome con cui entra: deve essere unico.">
          <input
            className="campo"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nome@azienda.it"
            autoFocus
          />
        </Campo>
        <Campo etichetta="Nome e cognome">
          <input className="campo" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Es. Valentina Migliorati" />
        </Campo>
        <Campo etichetta="Ruolo">
          <select className="campo" value={ruolo} onChange={(e) => setRuolo(e.target.value as 'ADMIN' | 'OPERATORE')}>
            {RUOLI.map((r) => (
              <option key={r.valore} value={r.valore}>
                {r.nome} — {r.spiega}
              </option>
            ))}
          </select>
        </Campo>
        <Campo
          etichetta="Password iniziale"
          aiuto="Almeno 10 caratteri. Serve solo per il primo accesso: gli verrà chiesto di cambiarla subito, così non resta in mano a te."
        >
          <input className="campo font-mono" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Campo>

        <p className="rounded-md bg-inchiostro-100 p-3 text-xs text-inchiostro-600">
          Comunicagli la password a voce o su un canale sicuro, non per email insieme all’indirizzo: al primo accesso
          Mastro gli chiederà di sceglierne una sua, e da lì in poi non la conoscerai più.
        </p>
      </div>

      <div className="mt-4 flex justify-end gap-2 border-t border-inchiostro-200 pt-4">
        <button className="bottone-secondario" onClick={onChiudi}>
          Annulla
        </button>
        <button className="bottone-primario" onClick={crea} disabled={attesa || !email.trim() || password.length < 10}>
          Crea l’utente
        </button>
      </div>
    </Dialogo>
  );
}

function FormPassword({
  utente,
  onChiudi,
  onFatto,
}: {
  utente: UtenteGestito;
  onChiudi: () => void;
  onFatto: () => void;
}) {
  const avvisa = useAvvisi();
  const [password, setPassword] = useState('');
  const [attesa, setAttesa] = useState(false);

  const salva = async () => {
    setAttesa(true);
    try {
      await api.post(`/api/utenti/${utente.id}/password`, { password });
      avvisa(`Password di ${utente.email} reimpostata: gli verrà chiesto di cambiarla al prossimo accesso.`);
      onFatto();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Operazione non riuscita.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  return (
    <Dialogo aperto titolo={`Password di ${utente.nome ?? utente.email}`} onChiudi={onChiudi}>
      <p className="mb-3 text-sm text-inchiostro-600">
        Serve quando qualcuno la dimentica. Quella vecchia non si può leggere — nemmeno da qui: si sostituisce e basta.
      </p>
      <Campo etichetta="Nuova password" aiuto="Almeno 10 caratteri. Dovrà cambiarla al primo accesso.">
        <input className="campo font-mono" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
      </Campo>
      <div className="mt-4 flex justify-end gap-2 border-t border-inchiostro-200 pt-4">
        <button className="bottone-secondario" onClick={onChiudi}>
          Annulla
        </button>
        <button className="bottone-primario" onClick={salva} disabled={attesa || password.length < 10}>
          Reimposta
        </button>
      </div>
    </Dialogo>
  );
}
